<script setup>
import { computed, onMounted, ref } from 'vue';
import { useI18n } from 'vue-i18n';
import { useAlert } from 'dashboard/composables';
import CopilotActionProposalsAPI from 'dashboard/api/copilot/actionProposals';
import CopilotActionExecutionsAPI from 'dashboard/api/copilot/actionExecutions';
import Button from 'dashboard/components-next/button/Button.vue';

const props = defineProps({
  proposalId: {
    type: Number,
    required: true,
  },
});

const emit = defineEmits(['updated']);

const { t } = useI18n();
const proposal = ref(null);
const executions = ref([]);
const editableActions = ref([]);
const isLoading = ref(false);
const isProcessing = ref(false);
const isEditing = ref(false);
const editError = ref('');

const actionCount = computed(() => proposal.value?.actions?.length || 0);
const canConfirm = computed(() => proposal.value?.status === 'pending');
const hasExecutions = computed(() => executions.value.length > 0);

const buildEditableActions = actions => {
  editableActions.value = (actions || []).map(action => ({
    ...action,
    payloadText: JSON.stringify(action.payload || {}, null, 2),
  }));
};

const fetchExecutions = async () => {
  const { data } = await CopilotActionExecutionsAPI.get({
    action_proposal_id: props.proposalId,
  });
  executions.value = data.action_executions || [];
};

const fetchProposal = async () => {
  isLoading.value = true;
  try {
    const { data } = await CopilotActionProposalsAPI.show(props.proposalId);
    proposal.value = data;
    buildEditableActions(data.actions);
    await fetchExecutions();
  } catch (error) {
    useAlert(error.message);
  } finally {
    isLoading.value = false;
  }
};

const runProposalAction = async action => {
  isProcessing.value = true;
  try {
    const { data } = await CopilotActionProposalsAPI[action](props.proposalId);
    proposal.value = data;
    await fetchExecutions();
    emit('updated', data);
  } catch (error) {
    useAlert(error.message);
  } finally {
    isProcessing.value = false;
  }
};

const removeAction = index => {
  editableActions.value = editableActions.value.filter(
    (_, idx) => idx !== index
  );
};

const saveProposal = async () => {
  editError.value = '';
  if (!editableActions.value.length) {
    editError.value = t('CAPTAIN.COPILOT.PROPOSAL.EDIT_EMPTY');
    return;
  }

  let actions;
  try {
    actions = editableActions.value.map(action => {
      const { payloadText, ...rest } = action;
      return {
        ...rest,
        payload: JSON.parse(payloadText || '{}'),
      };
    });
  } catch {
    editError.value = t('CAPTAIN.COPILOT.PROPOSAL.EDIT_INVALID_JSON');
    return;
  }

  isProcessing.value = true;
  try {
    const { data } = await CopilotActionProposalsAPI.update(props.proposalId, {
      action_proposal: { actions },
    });
    proposal.value = data;
    buildEditableActions(data.actions);
    isEditing.value = false;
    emit('updated', data);
  } catch (error) {
    useAlert(error.message);
  } finally {
    isProcessing.value = false;
  }
};

const undoExecution = async execution => {
  isProcessing.value = true;
  try {
    await CopilotActionExecutionsAPI.undo(execution.id);
    await fetchExecutions();
  } catch (error) {
    useAlert(error.message);
  } finally {
    isProcessing.value = false;
  }
};

const canUndo = execution => {
  return execution.reversible && execution.status === 'succeeded';
};

onMounted(fetchProposal);
</script>

<template>
  <div
    class="mt-3 rounded-xl border border-n-weak bg-n-alpha-2 p-3 text-n-slate-12"
  >
    <div v-if="isLoading" class="text-xs text-n-slate-11">
      {{ t('CAPTAIN.COPILOT.PROPOSAL.LOADING') }}
    </div>
    <template v-else-if="proposal">
      <div class="flex items-start justify-between gap-3">
        <div class="min-w-0">
          <div class="text-sm font-medium truncate">
            {{ proposal.title }}
          </div>
          <div class="mt-0.5 text-xs text-n-slate-11">
            {{
              t('CAPTAIN.COPILOT.PROPOSAL.SUMMARY', {
                count: actionCount,
                risk: proposal.risk_level,
                status: proposal.status,
              })
            }}
          </div>
        </div>
      </div>
      <div v-if="canConfirm" class="mt-3 space-y-2">
        <div class="flex items-center justify-between gap-2">
          <span class="text-xs font-medium text-n-slate-11">
            {{ t('CAPTAIN.COPILOT.PROPOSAL.ACTIONS_TITLE') }}
          </span>
          <button
            type="button"
            class="text-xs font-medium text-n-blue-11 hover:underline"
            @click="isEditing = !isEditing"
          >
            {{
              isEditing
                ? t('CAPTAIN.COPILOT.PROPOSAL.CLOSE_EDITOR')
                : t('CAPTAIN.COPILOT.PROPOSAL.EDIT')
            }}
          </button>
        </div>

        <div
          v-for="(action, index) in editableActions"
          :key="action.id || `${action.action_key}-${index}`"
          class="rounded-md border border-n-weak bg-n-solid-1 p-2"
        >
          <div class="flex items-center justify-between gap-2">
            <div class="min-w-0">
              <div class="text-xs font-medium text-n-slate-12 truncate">
                {{ action.action_key }}
              </div>
              <div class="text-[11px] text-n-slate-10 truncate">
                {{
                  t('CAPTAIN.COPILOT.PROPOSAL.TARGET', {
                    target: action.target_type || 'Target',
                    id: action.target_id,
                  })
                }}
              </div>
            </div>
            <button
              v-if="isEditing"
              type="button"
              class="shrink-0 text-xs font-medium text-n-ruby-11 hover:underline"
              @click="removeAction(index)"
            >
              {{ t('CAPTAIN.COPILOT.PROPOSAL.REMOVE_ACTION') }}
            </button>
          </div>

          <textarea
            v-if="isEditing"
            v-model="action.payloadText"
            rows="5"
            class="reset-base mt-2 w-full rounded-md border border-n-weak bg-n-alpha-2 px-2 py-1.5 font-mono text-xs text-n-slate-12 resize-y"
          />
        </div>

        <p v-if="editError" class="text-xs text-n-ruby-11">
          {{ editError }}
        </p>

        <div v-if="isEditing" class="flex items-center gap-2">
          <Button
            :label="t('CAPTAIN.COPILOT.PROPOSAL.SAVE_EDIT')"
            sm
            teal
            :is-loading="isProcessing"
            @click="saveProposal"
          />
          <Button
            :label="t('CAPTAIN.COPILOT.PROPOSAL.RELOAD')"
            sm
            slate
            faded
            :disabled="isProcessing"
            @click="fetchProposal"
          />
        </div>
      </div>
      <div v-if="canConfirm" class="mt-3 flex flex-wrap items-center gap-2">
        <Button
          :label="t('CAPTAIN.COPILOT.PROPOSAL.CONFIRM')"
          sm
          teal
          :is-loading="isProcessing"
          @click="runProposalAction('confirm')"
        />
        <Button
          :label="t('CAPTAIN.COPILOT.PROPOSAL.CANCEL')"
          sm
          slate
          faded
          :disabled="isProcessing"
          @click="runProposalAction('cancel')"
        />
      </div>
      <div v-if="hasExecutions" class="mt-3 space-y-2">
        <div
          v-for="execution in executions"
          :key="execution.id"
          class="rounded-md bg-n-alpha-2 px-2 py-1.5"
        >
          <div class="flex items-center justify-between gap-2">
            <span
              class="text-xs text-n-slate-11 truncate flex items-center gap-1"
            >
              <span>{{ execution.action_key }}</span>
              <span>{{ execution.status }}</span>
            </span>
            <button
              v-if="canUndo(execution)"
              type="button"
              class="shrink-0 text-xs font-medium text-n-blue-11 hover:underline disabled:opacity-50"
              :disabled="isProcessing"
              @click="undoExecution(execution)"
            >
              {{ t('CAPTAIN.COPILOT.PROPOSAL.UNDO') }}
            </button>
          </div>
          <p v-if="execution.error_message" class="mt-1 text-xs text-n-ruby-11">
            {{ execution.error_message }}
          </p>
        </div>
      </div>
    </template>
  </div>
</template>
